import React from 'react';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { analytics } from '../hooks/useAnalytics';

const STORAGE_KEY = 'ivy_upsell_dismissed';

function isDismissed(id) {
  if (typeof window === 'undefined') return false;
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const ts = data[id];
    if (!ts) return false;
    // Re-show after 7 days
    return (Date.now() - ts) < 7 * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function markDismissed(id) {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    data[id] = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
}

/**
 * Context-aware in-app upsell.
 * Examples:
 *   <UpsellPrompt context="dashboard-low-readiness" readiness={42} />
 *   <UpsellPrompt context="essay-stuck" />
 *   <UpsellPrompt context="school-list-empty" />
 */
export default function UpsellPrompt({ context, readiness, onDismiss }) {
  const [dismissed, setDismissed] = React.useState(() => isDismissed(context));

  const handleDismiss = () => {
    markDismissed(context);
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  // Pick copy based on context
  const variants = {
    'dashboard-low-readiness': {
      headline: `Your application is ${readiness ?? 0}% ready.`,
      body: 'Get a former Harvard admissions officer to review your full file before Early Decision — multi-advisor strategic review, unlimited essay feedback, interview coaching.',
      cta: 'Talk to a Strategist',
      to: '/pricing',
    },
    'dashboard-mid-readiness': {
      headline: 'Polish your application with a human strategist.',
      body: "You're past the foundation. The next 25% is where applications get accepted or deferred. Premium Advisory pairs you with a former admissions officer.",
      cta: 'Explore Premium Advisory',
      to: '/pricing',
    },
    'essay-stuck': {
      headline: 'Stuck on a draft?',
      body: 'Premium Advisory includes essay architecture sessions with former Yale, Harvard, and Columbia interviewers. Real feedback within 48 hours.',
      cta: 'Get Essay Review',
      to: '/pricing',
    },
    'school-list-empty': {
      headline: 'Not sure where to apply?',
      body: 'Schedule a free 30-min consultation. Our team helps families build a strategic reach / target / safety list based on your profile and goals.',
      cta: 'Schedule Free Consultation',
      to: '/consultation',
    },
  };

  const v = variants[context];
  if (!v) return null;

  return (
    <Card className="relative border-[#D4AF37]/30 bg-gradient-to-br from-[#0F392B] to-[#143726] text-white overflow-hidden shadow-md" data-testid={`upsell-${context}`}>
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 rounded-md hover:bg-white/10 transition-colors z-10"
        aria-label="Dismiss"
        data-testid={`upsell-dismiss-${context}`}
      >
        <X className="h-3.5 w-3.5 text-white/60" />
      </button>
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/8 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
      <CardContent className="p-5 relative">
        <div className="flex items-start gap-4">
          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-[#D4AF37]" />
          </div>
          <div className="flex-1 min-w-0 pr-6">
            <h3 className="text-sm font-semibold mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>{v.headline}</h3>
            <p className="text-xs text-white/70 leading-relaxed mb-3">{v.body}</p>
            <Link to={v.to} onClick={() => analytics.portalUpgradePromptClick(context)}>
              <Button size="sm" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 text-xs h-8 font-semibold" data-testid={`upsell-cta-${context}`}>
                {v.cta} <ArrowRight className="ml-1.5 h-3 w-3" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
